import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Dumbbell, Flag, Loader2, Timer, XCircle } from "lucide-react";
import { portalFetch, KyraApiError } from "@/lib/api";
import { usePlan } from "@/lib/queries";
import type {
  AbandonExecutionResponse,
  ExecutionDetailResponse,
  FinishExecutionResponse,
  StudentExecutionItem,
  StudentWorkout,
  StudentWorkoutItem,
  UpdateExecutionItemPayload,
  UpdateExecutionItemResponse,
} from "@/lib/types";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { ExerciseRow } from "@/components/exec/ExerciseRow";
import { ItemExecutionModal } from "@/components/exec/ItemExecutionModal";
import { MediaModal } from "@/components/exec/MediaModal";

// ─── Helpers ──────────────────────────────────────────────────────────────

interface FlatItem {
  blockName: string;
  blockId: string;
  item: StudentWorkoutItem;
}

function flattenWorkout(workout: StudentWorkout): FlatItem[] {
  const out: FlatItem[] = [];
  for (const block of workout.blocks) {
    for (const item of block.items) {
      out.push({ blockName: block.name, blockId: block.id, item });
    }
  }
  return out;
}

function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (hh > 0) return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
  return `${pad(mm)}:${pad(ss)}`;
}

// ─── View ─────────────────────────────────────────────────────────────────

/**
 * Tela imersiva de execução de treino. Renderiza num overlay `fixed inset-0`
 * que cobre o BottomNav do AppShell (full-screen). Porta da legada
 * `frontend/components/student-portal/WorkoutExecutionView.tsx` — aqui buscamos
 * os dados client-side (GET /executions/:id + usePlan pra montar o snapshot).
 */
export function WorkoutExecutionView() {
  const { slug = "", executionId = "" } = useParams();
  const navigate = useNavigate();

  // GET /executions/:id — execução + itens já registrados.
  const detailQuery = useQuery({
    queryKey: ["execution", executionId],
    queryFn: ({ signal }) =>
      portalFetch<ExecutionDetailResponse>(`/student-portal/me/executions/${executionId}`, {
        signal,
      }),
    enabled: !!executionId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Plano completo — pra puxar nome/mídia/prescrição (o endpoint de execução só
  // retorna IDs + valores reportados).
  const planQuery = usePlan();

  const isLoading = detailQuery.isLoading || planQuery.isLoading;
  const isError = detailQuery.isError || planQuery.isError;

  // Redireciona pro login se a sessão morreu.
  useEffect(() => {
    const err = detailQuery.error ?? planQuery.error;
    if (err instanceof KyraApiError && err.status === 401) {
      navigate(`/s/${slug}/login`, { replace: true });
    }
  }, [detailQuery.error, planQuery.error, navigate, slug]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-30 bg-canvas">
        <Spinner fullscreen />
      </div>
    );
  }

  if (isError || !detailQuery.data) {
    return (
      <div className="fixed inset-0 z-30 overflow-y-auto bg-canvas px-5 pt-8">
        <BackButton onClick={() => navigate(`/s/${slug}`)} />
        <EmptyState
          icon={Dumbbell}
          title="Não consegui carregar este treino"
          description="Tente voltar pro painel e iniciar de novo. Se persistir, fale com seu treinador."
        />
      </div>
    );
  }

  const execution = detailQuery.data.execution;
  const initialItems = detailQuery.data.items;
  const workout =
    planQuery.data?.workouts.find((w) => w.id === execution.workout_id) ?? null;

  if (!workout) {
    return (
      <div className="fixed inset-0 z-30 overflow-y-auto bg-canvas px-5 pt-8">
        <BackButton onClick={() => navigate(`/s/${slug}`)} />
        <EmptyState
          icon={Dumbbell}
          title="Treino indisponível"
          description="Este treino pode ter sido alterado pelo seu treinador. Volte pro painel."
        />
      </div>
    );
  }

  return (
    <ExecutionRunner
      slug={slug}
      execution={execution}
      initialItems={initialItems}
      workout={workout}
      onLeave={() => navigate(`/s/${slug}`, { replace: true })}
      onSessionExpired={() => navigate(`/s/${slug}/login`, { replace: true })}
    />
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border-subtle bg-elevated text-muted transition-colors hover:border-border-strong hover:bg-surface-hover hover:text-ink"
      aria-label="Voltar pro painel"
    >
      <ArrowLeft size={15} aria-hidden />
    </button>
  );
}

// ─── Runner (estado vivo) ──────────────────────────────────────────────────

interface ExecutionRunnerProps {
  slug: string;
  execution: ExecutionDetailResponse["execution"];
  initialItems: StudentExecutionItem[];
  workout: StudentWorkout;
  onLeave: () => void;
  onSessionExpired: () => void;
}

function ExecutionRunner({
  slug,
  execution,
  initialItems,
  workout,
  onLeave,
  onSessionExpired,
}: ExecutionRunnerProps) {
  // Estado dos itens, indexado por workout_item_id (imutável: novo Map a cada edição).
  const [itemsByWorkoutItemId, setItemsByWorkoutItemId] = useState<Map<string, StudentExecutionItem>>(
    () => {
      const map = new Map<string, StudentExecutionItem>();
      for (const it of initialItems) map.set(it.workout_item_id, it);
      return map;
    },
  );

  const [editingWorkoutItemId, setEditingWorkoutItemId] = useState<string | null>(null);
  const [previewItemId, setPreviewItemId] = useState<string | null>(null);
  const [closing, setClosing] = useState<"finishing" | "abandoning" | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  // ─── Cronômetro ─────────────────────────────────────────────────────────
  const startedAtMs = useMemo(() => {
    const ms = Date.parse(execution.executed_at);
    return Number.isFinite(ms) ? ms : Date.now();
  }, [execution.executed_at]);
  const [now, setNow] = useState<number>(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (closing) return;
    intervalRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [closing]);
  const elapsedSec = Math.max(0, Math.floor((now - startedAtMs) / 1000));

  // ─── Lista flat + progresso ───────────────────────────────────────────────
  const flatItems = useMemo(() => flattenWorkout(workout), [workout]);
  const totalItems = flatItems.length;
  const completedCount = useMemo(() => {
    let n = 0;
    for (const fi of flatItems) {
      const ex = itemsByWorkoutItemId.get(fi.item.id);
      if (ex?.completed_at) n += 1;
    }
    return n;
  }, [flatItems, itemsByWorkoutItemId]);
  const progressPct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  // ─── Lock scroll do body enquanto a tela imersiva está aberta ─────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ─── PATCH item (merge local imutável) ────────────────────────────────────
  const handleSaveItem = useCallback(
    async (
      workoutItemId: string,
      payload: UpdateExecutionItemPayload,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await portalFetch<UpdateExecutionItemResponse>(
          `/student-portal/me/executions/${execution.id}/items/${workoutItemId}`,
          { method: "PATCH", body: payload },
        );
        setItemsByWorkoutItemId((prev) => {
          const next = new Map(prev);
          next.set(workoutItemId, res.item);
          return next;
        });
        return { ok: true };
      } catch (err) {
        if (err instanceof KyraApiError && err.status === 401) {
          onSessionExpired();
          return { ok: false, error: "session_expired" };
        }
        console.error("[WorkoutExecutionView] PATCH item failed", err);
        return {
          ok: false,
          error:
            err instanceof KyraApiError
              ? `Não consegui salvar (status ${err.status}).`
              : "Não consegui salvar. Verifique sua conexão.",
        };
      }
    },
    [execution.id, onSessionExpired],
  );

  // ─── Finish / Abandon ─────────────────────────────────────────────────────
  async function handleFinish() {
    if (closing) return;
    setClosing("finishing");
    setCloseError(null);
    const durationMinutes = Math.max(1, Math.round(elapsedSec / 60));
    try {
      await portalFetch<FinishExecutionResponse>(
        `/student-portal/me/executions/${execution.id}/finish`,
        { method: "POST", body: { duration_minutes: durationMinutes } },
      );
      onLeave();
    } catch (err) {
      if (err instanceof KyraApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      console.error("[WorkoutExecutionView] finish failed", err);
      setCloseError("Não consegui finalizar agora. Tente de novo.");
      setClosing(null);
    }
  }

  async function handleAbandon() {
    if (closing) return;
    setClosing("abandoning");
    setCloseError(null);
    try {
      await portalFetch<AbandonExecutionResponse>(
        `/student-portal/me/executions/${execution.id}/abandon`,
        { method: "POST", body: {} },
      );
      onLeave();
    } catch (err) {
      if (err instanceof KyraApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      console.error("[WorkoutExecutionView] abandon failed", err);
      setCloseError("Não consegui abandonar agora. Tente de novo.");
      setClosing(null);
    }
  }

  // ─── Itens selecionados pro modal / preview ───────────────────────────────
  const editingFlat = useMemo(() => {
    if (!editingWorkoutItemId) return null;
    return flatItems.find((fi) => fi.item.id === editingWorkoutItemId) ?? null;
  }, [editingWorkoutItemId, flatItems]);
  const editingExisting = editingWorkoutItemId
    ? itemsByWorkoutItemId.get(editingWorkoutItemId) ?? null
    : null;

  const previewItem = useMemo(() => {
    if (!previewItemId) return null;
    return flatItems.find((fi) => fi.item.id === previewItemId)?.item ?? null;
  }, [previewItemId, flatItems]);

  // ─── Linhas (header de bloco quando muda) ─────────────────────────────────
  const rows: Array<
    { kind: "header"; blockId: string; blockName: string } | { kind: "item"; flat: FlatItem }
  > = [];
  let lastBlockId: string | null = null;
  for (const fi of flatItems) {
    if (fi.blockId !== lastBlockId) {
      rows.push({ kind: "header", blockId: fi.blockId, blockName: fi.blockName });
      lastBlockId = fi.blockId;
    }
    rows.push({ kind: "item", flat: fi });
  }

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-canvas">
      {/* HEADER — voltar, título, cronômetro, barra de progresso */}
      <header className="sticky top-0 z-20 border-b border-border-subtle bg-canvas/85 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-lg items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onLeave}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border-subtle bg-elevated text-muted transition-colors hover:border-border-strong hover:bg-surface-hover hover:text-ink"
            aria-label="Voltar pro painel"
          >
            <ArrowLeft size={15} aria-hidden />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-base text-ink sm:text-lg">{workout.title}</h1>
            <p className="truncate text-[11px] uppercase tracking-wider text-faint">Em execução</p>
          </div>
          <div
            className="flex shrink-0 items-center gap-2 rounded-xl border border-border-subtle bg-elevated px-3 py-2"
            aria-live="polite"
            aria-label="Tempo decorrido"
          >
            <Timer size={14} className="text-accent-soft" aria-hidden />
            <span className="font-mono text-sm tabular-nums text-ink">{formatTimer(elapsedSec)}</span>
          </div>
        </div>
        {/* Barra de progresso */}
        <div className="mx-auto w-full max-w-lg px-4 pb-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-elevated">
            <div
              className="h-full rounded-full bg-accent-soft transition-[width] duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
              role="progressbar"
              aria-valuenow={completedCount}
              aria-valuemin={0}
              aria-valuemax={totalItems}
              aria-label="Progresso do treino"
            />
          </div>
        </div>
      </header>

      {/* MAIN — lista de exercícios */}
      <main className="mx-auto w-full max-w-lg flex-1 overflow-y-auto px-4 py-5 pb-36">
        {totalItems === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-subtle bg-surface/40 p-8 text-center text-sm text-muted">
            Este treino não tem exercícios cadastrados. Avise seu treinador.
          </div>
        ) : (
          <div className="animate-fade-in space-y-4">
            {rows.map((row, idx) => {
              if (row.kind === "header") {
                return (
                  <h2
                    key={`h-${row.blockId}-${idx}`}
                    className="text-xs font-medium uppercase tracking-wider text-accent-soft"
                  >
                    {row.blockName}
                  </h2>
                );
              }
              const ex = itemsByWorkoutItemId.get(row.flat.item.id) ?? null;
              return (
                <ExerciseRow
                  key={row.flat.item.id}
                  item={row.flat.item}
                  existing={ex}
                  disabled={closing !== null}
                  slug={slug}
                  onEdit={() => setEditingWorkoutItemId(row.flat.item.id)}
                  onPreview={() => setPreviewItemId(row.flat.item.id)}
                />
              );
            })}
          </div>
        )}

        {closeError && (
          <div
            role="alert"
            className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          >
            {closeError}
          </div>
        )}
      </main>

      {/* FOOTER fixo — contador + ações */}
      <footer className="absolute inset-x-0 bottom-0 z-10 border-t border-border-subtle bg-canvas/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span className="text-ink">{completedCount}</span>
            <span>de</span>
            <span className="text-ink">{totalItems}</span>
            <span>{totalItems === 1 ? "exercício feito" : "exercícios feitos"}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmAbandon(true)}
              disabled={closing !== null}
              className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-elevated px-3 py-2 text-sm font-medium text-muted transition-colors hover:border-danger/60 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Abandonar treino"
            >
              <XCircle size={14} aria-hidden />
              Abandonar
            </button>
            <button
              type="button"
              onClick={handleFinish}
              disabled={closing !== null}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Concluir treino"
            >
              {closing === "finishing" ? (
                <>
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                  Finalizando…
                </>
              ) : (
                <>
                  <Flag size={14} aria-hidden />
                  Concluir treino
                </>
              )}
            </button>
          </div>
        </div>
      </footer>

      {/* MODAL — registro do item */}
      {editingFlat && (
        <ItemExecutionModal
          open
          onClose={() => setEditingWorkoutItemId(null)}
          workoutItem={editingFlat.item}
          existing={editingExisting}
          onSave={handleSaveItem}
        />
      )}

      {/* MODAL — demonstração */}
      {previewItem && (
        <MediaModal
          open
          onClose={() => setPreviewItemId(null)}
          title={previewItem.line}
          videoUrl={previewItem.video_url}
          gifPath={previewItem.gif_path}
          imageUrls={previewItem.image_urls}
        />
      )}

      {/* Confirmação de abandono */}
      {confirmAbandon && (
        <AbandonConfirm
          onCancel={() => setConfirmAbandon(false)}
          onConfirm={() => {
            setConfirmAbandon(false);
            handleAbandon();
          }}
          loading={closing === "abandoning"}
        />
      )}
    </div>
  );
}

function AbandonConfirm({
  onCancel,
  onConfirm,
  loading,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="abandon-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
    >
      <div className="animate-fade-in w-full max-w-sm rounded-2xl border border-border-subtle bg-surface/95 p-5 backdrop-blur-xl">
        <h2 id="abandon-title" className="font-display text-lg text-ink">
          Abandonar este treino?
        </h2>
        <p className="mt-2 text-sm text-muted">
          O que você já registrou fica salvo no histórico, mas a sessão é marcada como abandonada.
          Você pode iniciar um novo treino quando quiser.
        </p>
        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 rounded-xl border border-border-subtle bg-elevated px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-hover disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 rounded-xl bg-danger/85 px-3 py-2.5 text-sm font-medium text-ink transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Abandonando…" : "Abandonar"}
          </button>
        </div>
      </div>
    </div>
  );
}
